import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { CardSkeleton, InlineSkeleton } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { AttendanceDaysTable } from '@/modules/attendance/hr-my-attendance/components/attendance-days-table/attendance-days-table';
import { useEmployeeWorkStatusEligibility } from '@/modules/hr-directory/hooks/use-work-status-eligibility';
import { useGetOvertimeBalance, UserOvertimePanel } from '@/modules/overtime';
import { useDepartmentScope, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { AttendanceMonthlyGrid } from '../../components/attendance-monthly-grid/attendance-monthly-grid';
import { useGetTargetScope } from '../../hooks/use-manage-attendance';

export const UserAttendanceHistoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId = '' } = useParams<{ userId: string }>();
  const { canViewAll, departments, isLoading: isDepartmentScopeLoading } = useDepartmentScope();

  // A reviewer limited to some departments could otherwise reach any employee's history by typing
  // the URL directly, bypassing the list page's department filter — so re-check here that the
  // employee's department is one they may see. `VIEW_ALL_DEPARTMENTS` skips the check.
  const { data: targetScope, isLoading: isTargetScopeLoading } = useGetTargetScope(
    canViewAll ? undefined : userId
  );
  const isScopeLoading = !canViewAll && (isDepartmentScopeLoading || isTargetScopeLoading);
  const isOutOfScope =
    !canViewAll &&
    !isScopeLoading &&
    Boolean(targetScope) &&
    !departments.some((department) => department.ItemId === targetScope?.DepartmentId);

  // The employee's overtime, like the Overtime tab of My Attendance: for reviewers who may view
  // overtime, and only when overtime is on for the employee (they have a balance row). The Overtime
  // data rules still limit which rows come back.
  const canViewOvertime = useHasPermission(HRM_PERMISSIONS.OVERTIME.VIEW);
  const { data: overtimeBalance, isLoading: isOvertimeLoading } = useGetOvertimeBalance(
    canViewOvertime ? userId : ''
  );
  const hasOvertime = canViewOvertime && Boolean(overtimeBalance);
  const isOvertimeTabPending = canViewOvertime && isOvertimeLoading;

  const { byUserId, isLoading: isEmployeeLoading } = useEmployeesByUserIds([userId]);
  const employee = byUserId.get(userId);
  const employeeLine = [
    employee?.DisplayName ?? overtimeBalance?.ResourceName,
    employee?.Email,
    employee?.DepartmentName ?? overtimeBalance?.DepartmentName,
  ]
    .filter(Boolean)
    .join(' · ');

  // The Overtime and Break time columns follow the employee's own permissions (looked up in IAM),
  // not the reviewer's; they stay hidden while those aren't known.
  const eligibility = useEmployeeWorkStatusEligibility(userId || undefined);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label={t('BACK')}
          onClick={() => navigate('/hr/manage-attendance')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex min-w-0 flex-col">
          <h1 className="truncate text-lg font-semibold">{t('ATTENDANCE_HISTORY')}</h1>
          {!isOutOfScope && isEmployeeLoading && !employeeLine && (
            <InlineSkeleton className="mt-1 w-64 max-w-full" />
          )}
          {!isOutOfScope && employeeLine && (
            <span className="break-words text-sm text-muted-foreground">{employeeLine}</span>
          )}
        </div>
      </div>

      {isOutOfScope ? (
        <p className="text-sm text-muted-foreground">{t('ATTENDANCE_OUT_OF_DEPARTMENT_SCOPE')}</p>
      ) : (
        <Tabs defaultValue="list">
          {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
          <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
            <TabsTrigger value="list" className="max-sm:flex-1">
              {t('LIST_VIEW')}
            </TabsTrigger>
            <TabsTrigger value="calendar" className="max-sm:flex-1">
              {t('CALENDAR_VIEW')}
            </TabsTrigger>
            {hasOvertime && (
              <TabsTrigger value="overtime" className="max-sm:flex-1">
                {t('OVERTIME_HOURS')}
              </TabsTrigger>
            )}
            {/* Whether the employee has overtime is still being checked: hold the tab's place. */}
            {isOvertimeTabPending && <Skeleton className="mx-1 h-6 w-28 max-sm:flex-1" />}
          </TabsList>
          <TabsContent value="list">
            {/* Every day of the range, like the employee's own list; each day's correction shows
                as its status (none is requested from here). */}
            {isScopeLoading ? (
              <CardSkeleton lines={8} />
            ) : (
              <AttendanceDaysTable
                userId={userId}
                showBreak={eligibility?.breaks === true}
                showOvertime={eligibility?.overtime === true}
                showCorrectionStatus
              />
            )}
          </TabsContent>
          <TabsContent value="calendar">
            {isScopeLoading ? (
              <CardSkeleton bodyClassName="h-96 w-full" />
            ) : (
              <AttendanceMonthlyGrid userId={userId} />
            )}
          </TabsContent>
          {hasOvertime && (
            <TabsContent value="overtime">
              <UserOvertimePanel userId={userId} />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
};
